import { computed, toRef } from 'vue'
import { useState, useShopwareContext, useProduct } from '#imports'
import type { Schemas } from '#shopware'

export const COURSEFRAME_PERSONALIZATION_MAX_LENGTH = 35

interface CourseframeStateShape {
    selectedMatch: Schemas['CourseframeCoordinateMatch'] | null
    courseCoordinates: { latitude: number; longitude: number } | null
    searchTerm: string
    personalizedText: string
    lookupLoading: boolean
}

export function useCourseframe () {
    const state = useState<CourseframeStateShape>('courseframe', emptySelection)
    const { apiClient } = useShopwareContext()
    const { product } = useProduct()

    // The courseframe extension is only present on courseframe products; courseSelection additionally
    // requires the custom field courseframe_individual
    const courseSelection = computed(() => product.value?.extensions?.courseframe?.courseSelection === true)

    const selectedMatch = toRef(state.value, 'selectedMatch')
    const searchTerm = toRef(state.value, 'searchTerm')
    const personalizedText = toRef(state.value, 'personalizedText')
    const lookupLoading = computed(() => state.value.lookupLoading)

    const imageUrl = computed(() => state.value.selectedMatch?.imageUrl ?? null)

    const payload = computed<Partial<Schemas['AddToCartPayload']>>(() => {
        const printText = state.value.personalizedText
        const courseframe: Schemas['CourseframePayload'] = {}

        if (courseSelection.value) {
            // Sent even when empty: the backend treats a null id and 0/0 coordinates as "the search found
            // nothing" and falls back to the search term for the line item identity.
            courseframe.coordinateId = state.value.selectedMatch?.id ?? null
            courseframe.latitude = state.value.courseCoordinates?.latitude ?? 0
            courseframe.longitude = state.value.courseCoordinates?.longitude ?? 0

            if (state.value.searchTerm.trim() !== '') {
                courseframe.searchTerm = state.value.searchTerm
            }
        }

        // Untrimmed on purpose — the backend splits line items by the exact print text.
        if (printText.trim() !== '') {
            courseframe.courseFreeText = printText
        }

        if (Object.keys(courseframe).length === 0) return {}

        return {
            courseframe,
            // Kept next to courseFreeText until SAP reads the print text from the courseframe payload
            ...(printText.trim() !== '' && { courseframe_personalized_text: printText }),
        }
    })

    function hasEmoji (value: string): boolean {
        return /\p{Extended_Pictographic}/u.test(value)
    }

    async function lookupCoordinate (productId: string, latitude: number, longitude: number) {
        state.value.courseCoordinates = { latitude, longitude }
        state.value.lookupLoading = true

        try {
            const { data } = await apiClient.invoke('lookupCourseframeCoordinate post /courseframe/coordinate-lookup', {
                body: { productId, latitude, longitude },
            })

            state.value.selectedMatch = data.matches?.[0] ?? null
        } catch (e) {
            console.error(e)
            state.value.selectedMatch = null
        } finally {
            state.value.lookupLoading = false
        }
    }

    function clearMatch () {
        state.value.selectedMatch = null
        state.value.courseCoordinates = null
    }

    function reset () {
        Object.assign(state.value, emptySelection())
    }

    function emptySelection (): CourseframeStateShape {
        return {
            selectedMatch: null,
            courseCoordinates: null,
            searchTerm: '',
            personalizedText: '',
            lookupLoading: false,
        }
    }

    return {
        courseSelection,
        selectedMatch,
        searchTerm,
        personalizedText,
        lookupLoading,
        imageUrl,
        hasEmoji,
        lookupCoordinate,
        clearMatch,
        reset,
        payload,
    }
}
